import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { manageContent } from '../ResourcesContent/flag.on/helpers'
import { LAYOUT_WITH_ANCHOR_NAVIGATION } from './pageLayouts'
import {
  AnchoredArticleWrapper,
  AnchoredContentWrapper,
  AnchoredPageWrapper,
  AnchorNavigationWrapper,
  ContentWrapper,
  GrayBackground,
  PageWrapper,
} from './component.styles'

import AnchoredHeaderBodyCopy from 'experienceComponents/AnchoredHeaderBodyCopy'
import ArticleHeaderBodyCopy from 'experienceComponents/ArticleHeaderBodyCopy'
import AnchorNavigation from 'experienceComponents/AnchorNavigation'
import AssetDescription from 'experienceComponents/AssetDescription'
import BookDescription from 'experienceComponents/BookDescription'
import ContentLandingBanner from 'experienceComponents/ContentLandingBanner'
import H3LinkList from 'experienceComponents/H3LinkList'
import HeaderBodyCopy from 'experienceComponents/HeaderBodyCopy'
import InlineTranslation from 'components/InlineTranslation'
import LanguageToggle from 'experienceComponents/LanguageToggle'
import MultilingualPdfSelector from 'components/MultilingualPdfSelector'
import TextLinkWithFileIcon from 'components/TextLinkWithFileIcon'
import VideoFullWidth from 'experienceComponents/VideoFullWidth'
import ImageDescription from 'experienceComponents/ImageDescription'
import FlexWidthImage from 'components/FlexWidthImage'
import MediaBodyCopyComponent from 'experienceComponents/MediaBodyCopy'
import ResourcesContentAccordionContainer from 'components/ResourcesContentAccordion'

const ContentComponents = {
  accordion: ResourcesContentAccordionContainer,
  anchor: AnchoredHeaderBodyCopy,
  assetDescription: AssetDescription,
  book: BookDescription,
  category: H3LinkList,
  headerBodyCopy: HeaderBodyCopy,
  image: FlexWidthImage,
  imageBodyCopy: ImageDescription,
  inlineTranslation: InlineTranslation,
  langPdf: MultilingualPdfSelector,
  lang: LanguageToggle,
  textLinkIcon: TextLinkWithFileIcon,
  video: VideoFullWidth,
  mediaBodyCopy: MediaBodyCopyComponent,
}

const NonAnchoredContentComponents = {
  ...ContentComponents,
  headerBodyCopy: ArticleHeaderBodyCopy,
}

export class ResourcesContentV3 extends Component {
  static propTypes = {
    anchorNavigation: PropTypes.arrayOf(
      PropTypes.shape({
        hash: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
      }),
    ),
    content: PropTypes.arrayOf(
      PropTypes.shape({
        data: PropTypes.object.isRequired,
        name: PropTypes.string.isRequired,
      }),
    ),
    currentLocale: PropTypes.string,
    getEntryWithNewLocale: PropTypes.func,
    landingBanner: PropTypes.shape({
      text: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired,
    pageLayout: PropTypes.string.isRequired,
    productTabTitle: PropTypes.string,
  }

  componentDidMount() {
    this.syncDocumentTitle()
  }

  componentDidUpdate() {
    this.syncDocumentTitle()
  }

  syncDocumentTitle() {
    const { landingBanner, productTabTitle } = this.props
    const { title } = landingBanner || {}
    const fullTitle = `${productTabTitle} | ${title}`

    if (title && document.title !== fullTitle) {
      document.title = fullTitle
    }
  }

  getPageContent() {
    const { pageLayout } = this.props || {}

    if (pageLayout === LAYOUT_WITH_ANCHOR_NAVIGATION) {
      const { anchorNavigation } = this.props || {}
      return (
        <GrayBackground>
          <AnchoredPageWrapper>
            <AnchorNavigationWrapper>
              <AnchorNavigation links={anchorNavigation} />
            </AnchorNavigationWrapper>
            <AnchoredContentWrapper>
              {this.getAnchoredContentComponents()}
            </AnchoredContentWrapper>
          </AnchoredPageWrapper>
        </GrayBackground>
      )
    }

    return (
      <GrayBackground>
        <ContentWrapper>{this.getContentComponents()}</ContentWrapper>
      </GrayBackground>
    )
  }

  getAnchoredContentComponents() {
    const { content = [] } = this.props || {}
    const articleGroups = this.groupArticlesByAnchors(content)

    return articleGroups.map((articleGroup, wrapperIndex) =>
      manageContent(
        articleGroup,
        ContentComponents,
        this.getContentComponent.bind(this),
        contentComponents => (
          <AnchoredArticleWrapper key={`anchorGroup-${wrapperIndex}`}>
            {contentComponents}
          </AnchoredArticleWrapper>
        ),
      ),
    )
  }

  groupArticlesByAnchors(contents = []) {
    let insideBoundary = false

    return contents.reduce((result, content) => {
      const { name, data } = content
      if (name === 'lang') {
        const { boundary } = data || {}
        if (boundary) {
          insideBoundary = true
          return [...result, [content]]
        }
        insideBoundary = false
      }

      if (name === 'anchor' && !insideBoundary) {
        return [...result, [content]]
      }

      const lastIndex = result.length - 1
      const currentIndex = lastIndex < 0 ? 0 : lastIndex
      const currentGroup = result[currentIndex] || []
      const updatedCurrentGroup = [...currentGroup, content]
      const resultsLessLastItem = result.slice(0, result.length - 1)

      return [...resultsLessLastItem, updatedCurrentGroup]
    }, [])
  }

  getContentComponents() {
    const { content } = this.props || {}

    return manageContent(
      content,
      ContentComponents,
      this.getContentComponent.bind(this),
      contentComponents => contentComponents,
    )
  }

  getContentComponent(contentItem, index, nonAnchor) {
    const { name, data: componentProps } = contentItem || {}
    const { getEntryWithNewLocale, currentLocale } = this.props
    const Components = nonAnchor
      ? NonAnchoredContentComponents
      : ContentComponents
    const ContentComponent = Components[name] || null

    if (ContentComponent === null) {
      return null
    }

    if (name === 'image') {
      return (
        <ContentComponent
          key={`content-${index}-${name}`}
          {...componentProps}
        />
      )
    }

    return (
      <ContentComponent
        key={`content-${index}-${name}`}
        {...componentProps}
        getEntryWithNewLocale={getEntryWithNewLocale}
        locale={currentLocale}
      />
    )
  }

  render() {
    const { landingBanner } = this.props || {}

    return (
      <PageWrapper data-testid="resources-content-page">
        <ContentLandingBanner key="landingBanner" {...landingBanner} />
        {this.getPageContent()}
      </PageWrapper>
    )
  }
}

export default ResourcesContentV3
